import popoverStyles from '@/components/Atoms/Tooltip/PopoverContent.module.css';
import type { ToggleEvent } from 'react';
import { useId, useRef, useState } from 'react';

export const useHintPopover = <T extends HTMLElement = HTMLSpanElement>() => {
    const popoverRef = useRef<T>(null);
    const popoverId = useId();
    const [isOpen, setIsOpen] = useState(false);

    const openPopover = () => {
        const popover = popoverRef.current;
        if (popover && !popover.matches(':popover-open')) {
            popover.showPopover();
        }
    };

    const closePopover = () => {
        const popover = popoverRef.current;
        if (popover && popover.matches(':popover-open')) {
            popover.hidePopover();
        }
    };

    const togglePopover = () => {
        const popover = popoverRef.current;
        if (!popover) {
            return;
        }

        if (popover.matches(':popover-open')) {
            popover.hidePopover();
            return;
        }

        popover.showPopover();
    };

    return {
        popoverRef,
        openPopover,
        closePopover,
        togglePopover,
        isOpen,
        anchorScopeClassName: popoverStyles.anchorScope,
        triggerProps: {
            className: popoverStyles.anchorTrigger,
            'aria-describedby': popoverId,
            onMouseEnter: openPopover,
            onMouseLeave: closePopover,
            onFocus: openPopover,
            onBlur: closePopover,
        },
        popoverProps: {
            id: popoverId,
            ref: popoverRef,
            role: 'tooltip',
            popover: 'hint' as const,
            className: popoverStyles.anchoredPopover,
            // beforetoggle fires synchronously, so lazily rendered content is ready before the popover paints
            onBeforeToggle: (event: ToggleEvent<T>) => setIsOpen(event.newState === 'open'),
        },
    };
};
